import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";

import { AREAS, areaForPath, visibleTabs } from "./areas";
import AppBar from "./components/AppBar";
import EADock from "./components/EADock";
import XNav from "./components/XNav";
import YNav from "./components/YNav";
import ErrorBoundary from "./ErrorBoundary";
import { OverlayHost, useOverlayStack } from "./overlays";
import { ShellContext } from "./ShellContext";
import "./cipher-system.css";
import "./shell.css";
// LAST on purpose. CSS import order decides precedence at equal specificity,
// so the glass layer must come after both stylesheets it refines. It sits on
// top of `cipher-system.css` rather than editing it: that file is a
// hand-maintained mirror of design/system/cipher.css and carries its own
// drift warning. Presentation only - no markup, no data, no copy.
import "./shell-glass.css";

/**
 * The Cipher v1.1 global shell. Composed once; every surface renders inside it.
 *
 * TWO AXES, AND WHERE EACH ONE'S TRUTH LIVES (v1.1 §10 — LOCKED)
 * =============================================================
 * The Y-axis is the same five areas everywhere and comes from `areas.js`. The
 * X-axis changes per area and comes from the same file, keyed by the active
 * area. Neither is passed in by a surface, so a surface cannot quietly grow a
 * sixth Y-axis entry or reorder its own tabs — §2 locks both, and the shell is
 * where that lock is expressed as code rather than as a comment.
 *
 * The active area is derived from the URL rather than held in state. State
 * would have to be kept in step with the router by hand, and the first thing
 * that breaks is the browser's own back button.
 *
 * WHAT THE SHELL OWNS
 * ===================
 *   Y-axis, role-filtered            X-axis, per active area
 *   appbar, with slots               page frame
 *   the overlay stack                the EA dock
 *
 * THE ONE THING THAT ARRIVES FROM ABOVE
 * =====================================
 * `onSignOut`. The shell holds no session and calls no auth endpoint — it takes
 * the handler from `V2Routes`, which owns `useAuth()`, and hands it to the
 * account chip. Passing it rather than importing `useAuth` here is what keeps
 * every shell module renderable without the whole of `App.js` behind it.
 *
 * WHAT IT DOES NOT OWN
 * ====================
 * Anything inside the page. Every surface is a `routes/*.jsx` module owned by
 * one session, and the shell renders it without knowing what it is.
 */
export default function CipherShell({ user, onSignOut, children }) {
  const location = useLocation();
  const { stack, api } = useOverlayStack();

  const area = areaForPath(location.pathname) || AREAS[0];

  // "" for an area's home, otherwise the first path segment after the area.
  const tab = useMemo(() => {
    const rest = location.pathname.slice(area.path.length);
    const seg = rest.replace(/^\//, "").split("/")[0] || "";
    return seg;
  }, [location.pathname, area.path]);

  const shell = useMemo(
    () => ({ user, area: area.id, tab, overlays: api }),
    [user, area.id, tab, api]
  );

  // §8.1: the dock follows the operator everywhere and is hidden only on the
  // dedicated EA area — where the assistant is the page, and a dock offering to
  // open the thing already open is noise.
  const showDock = area.id !== "ea";

  // The appbar's left slot carries the area crumb plus the active tab, so the
  // operator can read where they are without looking at two navs. On an area
  // home there is no tab and the crumb stands alone.
  const activeTab = visibleTabs(area.id, user).find((t) => t.tab === tab);
  const crumb = activeTab && activeTab.tab
    ? `${area.label} · ${activeTab.label}`
    : area.label;

  return (
    <ShellContext.Provider value={shell}>
      <div className="app cipher-v2 cipher-ui">
        <YNav user={user} onSignOut={onSignOut} />

        <div className="shell-body">
          <AppBar areaLabel={crumb} />
          <XNav areaId={area.id} basePath={area.path} user={user} />

          <main className="main" id="cipher-main">
            {/* Per-area containment. A surface that throws leaves both navs, the
                appbar and the dock alive, so the operator can go elsewhere
                instead of reloading. Keyed on the pathname rather than wrapped
                in a `key`, so navigating clears the latch without remounting —
                see ErrorBoundary.jsx. */}
            {/* The area's id travels with the page so an area can own its own
                width. WORKSTATION-VISUAL-ROLLOUT, 2026-09-10: `.cipher-v2 .page`
                caps EVERY area at 1280px, which is the empty right third the
                Founder saw on Exploration and again on the Workstation. Brain
                wants that cap; the Workstation does not. Without a hook like
                this the only options were to change the cap for everything or
                to fight it from inside a child. */}
            <div className={`page area-${area.id}`}>
              <ErrorBoundary resetKey={location.pathname} label={crumb}>
                {children}
              </ErrorBoundary>
            </div>
          </main>
        </div>

        {showDock ? (
          <EADock user={user} context={{ area: area.id, tab }} />
        ) : null}

        <OverlayHost
          stack={stack}
          onClose={api.close}
          onCloseTop={api.closeTop}
        />
      </div>
    </ShellContext.Provider>
  );
}
